<template>
  <login-panel class="register-secret-question" xs>
    <div class="register-secret-question__content">
      <h3 class="register-secret-question__title">
        {{ $t('login.register-secret-question') }}
      </h3>
      <p class="register-secret-question__description">
        {{ $t('login.register-secret-question-text') }}
      </p>
      <p class="register-secret-question__tip">
        {{ $t('login.register-secret-question-tip') }}
      </p>
      <div class="register-secret-question__form">
        <label class="register-secret-question__label" for="question">
          {{ $t('login.secret-question') }}
        </label>
        <b-form-input
          id="question"
          class="text-uppercase"
          v-model="question"
          type="text"
          :state="questionState"
          maxlength="150"
          minlength="10"
        ></b-form-input>
        <b-tooltip
          :show="showQuestionTooltip()"
          target="question"
          triggers=""
          placement="right"
          boundary="window"
          :fallback-placement="['right']"
        >
          {{ questionTooltipMessage() }}
        </b-tooltip>
        <label class="register-secret-question__label" for="answer">
          {{ $t('login.answer') }}
        </label>
        <b-form-input
          id="answer"
          class="text-uppercase"
          v-model="answer"
          type="text"
          :state="answerState"
          maxlength="100"
          minlength="3"
        ></b-form-input>
        <b-tooltip
          :show="showAnswerTooltip()"
          target="answer"
          triggers=""
          placement="right"
          boundary="window"
          :fallback-placement="['right']"
        >
          {{ answerTooltipMessage() }}
        </b-tooltip>
        <div class="register-secret-question__form-buttons">
          <b-button variant="primary"
            id="registerQuestion"
            class="register-secret-question__btn-register-question"
            :disabled="$v.$invalid"
            block
            @click="saveSecretQuestion"
          >
            {{ $t('login.register-question') }}
          </b-button>
          <b-button
            id="cancel"
            class="register-secret-question__btn-later"
            variant="outline-primary"
            block
            @click="cancel"
            v-if="!isOffLineMode"
          >
            {{ $t('login.dont-want-to-do-that-now') }}
          </b-button>
        </div>
      </div>
    </div>
  </login-panel>
</template>

<script>
import { mapGetters } from 'vuex';
import { required } from 'vuelidate/lib/validators';
import LoginPanel from '@/components/LoginPanel';
import AuthenticationService from '@/services/authentication';
import SecretQuestionService from '@/domain/services/login/secretQuestionService';

export default {
  constants: {
    QUESTION_MIN_LENGTH: 10,
    QUESTION_MAX_LENGTH: 150,
    ANSWER_MIN_LENGTH: 3,
    ANSWER_MAX_LENGTH: 100,
  },
  components: {
    LoginPanel,
  },
  data: () => ({
    question: '',
    answer: '',
    submitAttempted: false,
    activeInvalidField: null,
  }),
  validations: {
    question: {
      required,
    },
    answer: {
      required,
    },
  },
  computed: {
    ...mapGetters([
      'isOffLineMode',
    ]),
    questionState() {
      return this.$bsStateValidator(
        this.submitAttempted
          && this.activeInvalidField === 'question'
          && this.isQuestionLengthInvalid(),
      );
    },
    answerState() {
      return this.$bsStateValidator(
        this.submitAttempted
          && this.activeInvalidField === 'answer'
          && this.isAnswerLengthInvalid(),
      );
    },
  },
  methods: {
    isQuestionRequiredInvalid() {
      return !this.$v.question.required;
    },
    isAnswerRequiredInvalid() {
      return !this.$v.answer.required;
    },
    isQuestionLengthInvalid() {
      const questionLength = (this.question || '').length;

      return questionLength < this.$options.constants.QUESTION_MIN_LENGTH
        || questionLength > this.$options.constants.QUESTION_MAX_LENGTH;
    },
    isAnswerLengthInvalid() {
      const answerLength = (this.answer || '').length;

      return answerLength < this.$options.constants.ANSWER_MIN_LENGTH
        || answerLength > this.$options.constants.ANSWER_MAX_LENGTH;
    },
    showLengthRangeAlert(minLength, maxLength) {
      this.$pushAlert({
        code: 'MG001',
        messageParams: {
          0: String(minLength),
          1: String(maxLength),
        },
      });
    },
    saveSecretQuestion() {
      this.submitAttempted = true;
      this.activeInvalidField = null;

      if (this.isQuestionRequiredInvalid()) {
        this.activeInvalidField = 'question';
        return;
      }

      if (this.isQuestionLengthInvalid()) {
        this.activeInvalidField = 'question';
        this.showLengthRangeAlert(
          this.$options.constants.QUESTION_MIN_LENGTH,
          this.$options.constants.QUESTION_MAX_LENGTH,
        );
        return;
      }

      if (this.isAnswerRequiredInvalid()) {
        this.activeInvalidField = 'answer';
        return;
      }

      if (this.isAnswerLengthInvalid()) {
        this.activeInvalidField = 'answer';
        this.showLengthRangeAlert(
          this.$options.constants.ANSWER_MIN_LENGTH,
          this.$options.constants.ANSWER_MAX_LENGTH,
        );
        return;
      }

      SecretQuestionService.saveSecretQuestion({
        pergunta: this.question,
        resposta: this.answer,
      }).then(() => this.$pushAlert({ code: 'MS001' }))
        .then(() => this.doLogout());
    },
    showQuestionTooltip() {
      return this.submitAttempted
        && this.activeInvalidField === 'question'
        && (this.isQuestionRequiredInvalid() || this.isQuestionLengthInvalid());
    },
    showAnswerTooltip() {
      return this.submitAttempted
        && this.activeInvalidField === 'answer'
        && (this.isAnswerRequiredInvalid() || this.isAnswerLengthInvalid());
    },
    questionTooltipMessage() {
      if (this.isQuestionRequiredInvalid()) {
        return this.$t('messages.required.message', {
          field: this.$t('login.secret-question'),
        });
      }

      return this.$t('messages.MG001.message', {
        0: String(this.$options.constants.QUESTION_MIN_LENGTH),
        1: String(this.$options.constants.QUESTION_MAX_LENGTH),
      });
    },
    answerTooltipMessage() {
      if (this.isAnswerRequiredInvalid()) {
        return this.$t('messages.required.message', {
          field: this.$t('login.answer'),
        });
      }

      return this.$t('messages.MG001.message', {
        0: String(this.$options.constants.ANSWER_MIN_LENGTH),
        1: String(this.$options.constants.ANSWER_MAX_LENGTH),
      });
    },
    doLogout() {
      AuthenticationService.logout();
    },
    cancel() {
      this.doLogout();
    },
  },
};
</script>

<style lang="sass" scoped>
.register-secret-question
  &__content
    display: flex
    flex-direction: column
  &__title
    font-weight: 600
    font-size: 1.1em
    text-transform: uppercase
    text-align: center
    margin-bottom: 20px
  &__form
    margin-top: 10px
  &__form-buttons
    margin-top: 20px
  &__label
    margin-top: 10px
  &__description
    text-align: justify
  &__tip
    color: var(--color-primary)
    font-size: .75em
</style>
